import { supabase } from "@/integrations/supabase/client";

export interface ConsentData {
  consentType: string;
  documentVersion: string;
  userAgent?: string;
}

const PENDING_KEY = 'pending_consent_replay';

interface PendingConsent extends ConsentData {
  userId: string;
  queuedAt: string;
}

const readQueue = (): PendingConsent[] => {
  try {
    return JSON.parse(localStorage.getItem(PENDING_KEY) || '[]');
  } catch {
    return [];
  }
};

const writeQueue = (items: PendingConsent[]) => {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(items));
  } catch {
    // ignore
  }
};

/**
 * Hook for logging user consents with server-side IP capture.
 * Uses edge function for GDPR Art. 7(1) compliant consent proof.
 *
 * The edge function accepts two modes:
 *  1. Authenticated session (preferred).
 *  2. Signup-grace: no session yet (email confirmation pending) — caller
 *     passes `userId`, server verifies the user is < 5 minutes old.
 *
 * If both edge + direct insert fail, the consent is queued in localStorage
 * and replayed on next authenticated load via `replayQueuedConsents`.
 */
export const useConsentTracking = () => {
  const logConsent = async (data: ConsentData, userId: string): Promise<boolean> => {
    try {
      // Pass userId so the edge function can use the signup-grace path
      // when no session exists yet.
      const { data: result, error } = await supabase.functions.invoke('log-consent', {
        body: {
          consentType: data.consentType,
          documentVersion: data.documentVersion,
          userAgent: data.userAgent || navigator.userAgent,
          userId,
        },
      });

      if (!error && result?.success) {
        return true;
      }

      if (error) {
        console.warn(
          `[Consent] Edge function failed (${data.consentType}@${data.documentVersion}):`,
          error.message,
        );
      }

      // Fallback: direct insert (loses server-side IP, but Art. 7(1) proof
      // via user_id + version + user_agent still holds). Requires an active
      // session because of RLS on user_consents.
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { error: fallbackError } = await supabase
          .from('user_consents')
          .insert({
            user_id: userId,
            consent_type: data.consentType,
            document_version: data.documentVersion,
            user_agent: data.userAgent || navigator.userAgent,
          });
        if (!fallbackError) {
          console.warn('[Consent] Recorded via direct insert (no server IP).');
          return true;
        }
        console.error('[Consent] Direct-insert fallback failed:', fallbackError);
      }

      // Last resort: queue for replay after next sign-in.
      const queue = readQueue();
      queue.push({ ...data, userId, queuedAt: new Date().toISOString() });
      writeQueue(queue);
      console.warn('[Consent] Queued for replay — no proof persisted yet.');
      return false;
    } catch (error) {
      console.error('Unexpected error logging consent:', error);
      return false;
    }
  };

  const getClientInfo = () => ({ userAgent: navigator.userAgent });

  return { logConsent, getClientInfo };
};

/**
 * Replay any queued consents now that the user is authenticated. Safe to
 * call on every authenticated app boot — drains the queue best-effort.
 */
export const replayQueuedConsents = async (userId: string): Promise<void> => {
  const queue = readQueue();
  if (queue.length === 0) return;

  const remaining: PendingConsent[] = [];
  for (const item of queue) {
    if (item.userId !== userId) {
      remaining.push(item);
      continue;
    }
    try {
      const { data, error } = await supabase.functions.invoke('log-consent', {
        body: {
          consentType: item.consentType,
          documentVersion: item.documentVersion,
          userAgent: item.userAgent,
          userId,
        },
      });
      if (error || !data?.success) {
        remaining.push(item);
      }
    } catch {
      remaining.push(item);
    }
  }
  writeQueue(remaining);
};
